import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from 'react-query';
import { adminStatsAPI, adminMatchesAPI } from '../services/adminApi';
import AdminScoreModal from './AdminScoreModal';
import { consumeAdminNavHandoff } from '../utils/adminNavFilters';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const BRAND = { blue: '#043663', red: '#D52B1E', muted: '#64748b' };

const Page = styled.div`
  max-width: 1280px;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
`;

const TitleBlock = styled.div`
  h2 {
    margin: 0 0 0.35rem;
    font-size: 1.65rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  p {
    margin: 0;
    color: ${BRAND.muted};
    font-size: 0.9rem;
    max-width: 40rem;
    line-height: 1.45;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  display: block;
  margin-bottom: 0.2rem;
`;

const Select = styled.select`
  min-width: 160px;
  padding: 0.45rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #fff;
  color: #0f172a;
`;

const Input = styled.input`
  min-width: 140px;
  padding: 0.45rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
`;

const Btn = styled.button`
  padding: 0.5rem 0.9rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
  background: ${(p) => (p.$primary ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : BRAND.blue)};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button`
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? BRAND.blue : '#e2e8f0')};
  background: ${(p) => (p.$active ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#475569')};
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Kpi = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const KpiLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
`;

const KpiValue = styled.div`
  margin-top: 0.35rem;
  font-size: 1.45rem;
  font-weight: 800;
  color: ${(p) => (p.$alert ? BRAND.red : BRAND.blue)};
  line-height: 1.15;
`;

const KpiHint = styled.div`
  margin-top: 0.25rem;
  font-size: 0.72rem;
  color: #94a3b8;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.15rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const PanelTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
`;

const Disclaimer = styled.div`
  font-size: 0.8rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    padding: 0.55rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
  }
  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 700;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    &:hover {
      color: ${BRAND.blue};
    }
  }
  td {
    color: #334155;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
`;

const BarTrack = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  min-width: 80px;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${(p) => Math.min(100, p.$pct || 0)}%;
  background: linear-gradient(90deg, ${BRAND.blue}, #0a558c);
  border-radius: 999px;
`;

const Empty = styled.div`
  padding: 2rem;
  text-align: center;
  color: #64748b;
`;

const ClickableStatus = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: ${BRAND.blue};
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  text-align: left;
  &:hover {
    color: ${BRAND.red};
  }
`;

const DrillPanel = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;

const DrillHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  h4 {
    margin: 0;
    font-size: 0.95rem;
    color: #0f172a;
  }
  p {
    margin: 0.25rem 0 0;
    font-size: 0.8rem;
    color: ${BRAND.muted};
  }
`;

const MatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 420px;
  overflow: auto;
`;

const MatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const MatchMeta = styled.div`
  flex: 1;
  min-width: 200px;
  font-size: 0.85rem;
  color: #334155;
  strong {
    color: #0f172a;
  }
  span {
    display: block;
    color: #64748b;
    font-size: 0.75rem;
    margin-top: 0.15rem;
  }
`;

const Pager = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.85rem;
  font-size: 0.8rem;
  color: ${BRAND.muted};
`;

const NotSetCard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
`;

const NotSetStat = styled.div`
  font-size: 0.75rem;
  color: #9a3412;
  strong {
    display: block;
    font-size: 1.15rem;
    color: #7c2d12;
    margin-top: 0.15rem;
  }
`;

const DRILLABLE_STATUSES = new Set(['score_submitted', 'disputed']);
const CLUBS_PAGE_SIZE = 20;

/** Fold accents so “Chambésy” matches “Chambesy” / “chambesy”. */
function foldSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'canton', label: 'By canton' },
  { id: 'skill', label: 'By skill' },
  { id: 'matches', label: 'Matches' },
  { id: 'groups', label: 'Group sizes' },
  { id: 'trends', label: 'Trends' },
  { id: 'ops', label: 'Attention' },
  { id: 'clubs', label: 'Home club (text)' },
];

function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return dir === 'asc' ? av - bv : bv - av;
    }
    return dir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });
  return copy;
}

function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => c.label).join(',');
  const lines = rows.map((row) =>
    columns
      .map((c) => {
        const v = row[c.key];
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      })
      .join(',')
  );
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function SortableHead({ label, colKey, sort, onSort }) {
  const active = sort.key === colKey;
  return (
    <th onClick={() => onSort(colKey)}>
      {label}
      {active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

export default function AdminLeagueAnalytics() {
  const { adminUser } = useAdminAuth();
  const canSeeFinancials =
    adminUser?.isSuperAdmin === true || adminUser?.role === 'super_admin';
  const queryClient = useQueryClient();
  const [preferredPhaseId, setPreferredPhaseId] = useState('');
  const [canton, setCanton] = useState('');
  const [cantonDraft, setCantonDraft] = useState('');
  const [tab, setTab] = useState('overview');
  const [sort, setSort] = useState({ key: 'registrations', dir: 'desc' });
  const [drillStatus, setDrillStatus] = useState(null);
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  const [clubSearch, setClubSearch] = useState('');
  const [clubPage, setClubPage] = useState(1);
  const [clubTeamSearch, setClubTeamSearch] = useState('');
  const [clubTeamPage, setClubTeamPage] = useState(1);
  const [clubDetailKey, setClubDetailKey] = useState(null); // filter team list to one club
  const clubTeamsSectionRef = useRef(null);

  // Additive: apply one-shot handoff from Attention / League Statistics
  useEffect(() => {
    const h = consumeAdminNavHandoff();
    if (!h) return;
    if (h.section && h.section !== 'analytics') return;
    if (h.seasonId != null && h.seasonId !== '') setPreferredPhaseId(String(h.seasonId));
    if (h.canton != null && h.canton !== '') {
      setCanton(String(h.canton));
      setCantonDraft(String(h.canton));
    }
    if (h.analyticsTab) setTab(String(h.analyticsTab));
    if (h.matchStatus) setDrillStatus(String(h.matchStatus));
  }, []);

  const {
    campaignId,
    seasonId,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allowAll,
  } = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
    preferredPhaseId,
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['admin-league-analytics', campaignId, seasonId, canton],
    () =>
      adminStatsAPI.getLeagueAnalytics({
        campaignId: campaignId || undefined,
        seasonId: seasonId || undefined,
        canton: canton || undefined,
      }),
    { keepPreviousData: true }
  );

  const {
    data: drillMatchesRes,
    isLoading: drillLoading,
    refetch: refetchDrill,
  } = useQuery(
    ['admin-analytics-match-drill', drillStatus, campaignId, seasonId],
    () =>
      adminMatchesAPI.getAll({
        status: drillStatus,
        season_id: seasonId || undefined,
      }),
    { enabled: !!drillStatus && DRILLABLE_STATUSES.has(drillStatus) }
  );

  const drillMatches =
    drillMatchesRes?.data?.matches ||
    drillMatchesRes?.data?.data?.matches ||
    drillMatchesRes?.matches ||
    [];

  const openDrill = (status) => {
    if (!DRILLABLE_STATUSES.has(status)) return;
    setDrillStatus((prev) => (prev === status ? null : status));
  };

  const onScoreModalSuccess = async () => {
    setScoreModalMatch(null);
    await Promise.all([refetchDrill(), refetch()]);
    queryClient.invalidateQueries('scheduledMatches');
    queryClient.invalidateQueries(['admin-league-analytics']);
  };

  const payload = data?.data?.data || data?.data || data;
  const kpis = payload?.kpis || {};
  const disclaimers = payload?.disclaimers || {};
  const showFinancials = canSeeFinancials && payload?.financialsVisible !== false;

  const onSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );
  };

  const cantonRows = useMemo(
    () => sortRows(payload?.byCanton || [], sort.key, sort.dir),
    [payload?.byCanton, sort]
  );
  const skillRows = useMemo(
    () => sortRows(payload?.bySkill || [], sort.key === 'registrations' ? 'teams' : sort.key, sort.dir),
    [payload?.bySkill, sort]
  );
  const matchRows = useMemo(
    () => sortRows(payload?.matchesByStatus || [], sort.key === 'registrations' ? 'count' : sort.key, sort.dir),
    [payload?.matchesByStatus, sort]
  );
  const groupRows = useMemo(
    () =>
      sortRows(
        payload?.groupSizes || [],
        sort.key === 'registrations' ? 'groupSize' : sort.key,
        sort.dir
      ),
    [payload?.groupSizes, sort]
  );
  const trendRows = useMemo(
    () => sortRows(payload?.registrationTrend || [], sort.key === 'registrations' ? 'month' : sort.key, sort.dir),
    [payload?.registrationTrend, sort]
  );
  const clubRowsAll = useMemo(
    () =>
      sortRows(
        payload?.homeClubs || [],
        sort.key === 'registrations' || sort.key === 'players' || sort.key === 'match_appearances' || sort.key === 'team_matches'
          ? 'matches'
          : sort.key === 'completed_appearances'
            ? 'completed_matches'
            : sort.key === 'distinct_matches'
              ? 'matches'
              : sort.key,
        sort.dir
      ),
    [payload?.homeClubs, sort]
  );
  const clubNotSet = useMemo(
    () =>
      clubRowsAll.find(
        (r) =>
          String(r.club_key || '').toLowerCase() === '(not set)' ||
          String(r.club_label || '').toLowerCase() === '(not set)'
      ) || null,
    [clubRowsAll]
  );
  const clubMeta = payload?.homeClubMeta || {};
  const clubNamedFiltered = useMemo(() => {
    const named = clubRowsAll.filter(
      (r) =>
        String(r.club_key || '').toLowerCase() !== '(not set)' &&
        String(r.club_label || '').toLowerCase() !== '(not set)'
    );
    const q = foldSearchText(clubSearch);
    if (!q) return named;
    return named.filter((r) => foldSearchText(r.club_label).includes(q));
  }, [clubRowsAll, clubSearch]);
  const clubPageCount = Math.max(1, Math.ceil(clubNamedFiltered.length / CLUBS_PAGE_SIZE));
  const clubPageSafe = Math.min(clubPage, clubPageCount);
  const clubRowsPage = useMemo(() => {
    const start = (clubPageSafe - 1) * CLUBS_PAGE_SIZE;
    return clubNamedFiltered.slice(start, start + CLUBS_PAGE_SIZE);
  }, [clubNamedFiltered, clubPageSafe]);

  const clubTeamRowsAll = useMemo(() => {
    let rows = payload?.homeClubTeams || [];
    if (clubDetailKey) {
      const selectedLabel = foldSearchText(
        clubRowsAll.find((c) => String(c.club_key) === String(clubDetailKey))?.club_label || ''
      );
      rows = rows.filter((r) => {
        if (String(r.club_key) === String(clubDetailKey)) return true;
        if (selectedLabel && foldSearchText(r.club_label) === selectedLabel) return true;
        return false;
      });
    }
    const q = foldSearchText(clubTeamSearch);
    if (q) {
      rows = rows.filter(
        (r) =>
          foldSearchText(r.team_name).includes(q) || foldSearchText(r.club_label).includes(q)
      );
    }
    return sortRows(
      rows,
      sort.key === 'registrations' || sort.key === 'team_matches' || sort.key === 'match_appearances' || sort.key === 'matches'
        ? 'matches_played'
        : sort.key === 'completed_matches' || sort.key === 'completed_appearances'
          ? 'matches_completed'
          : sort.key === 'club_label'
            ? 'club_label'
            : sort.key === 'team_name'
              ? 'team_name'
              : 'matches_played',
      sort.dir
    );
  }, [payload?.homeClubTeams, clubDetailKey, clubTeamSearch, sort, clubRowsAll]);
  const clubTeamPageCount = Math.max(1, Math.ceil(clubTeamRowsAll.length / CLUBS_PAGE_SIZE));
  const clubTeamPageSafe = Math.min(clubTeamPage, clubTeamPageCount);
  const clubTeamRowsPage = useMemo(() => {
    const start = (clubTeamPageSafe - 1) * CLUBS_PAGE_SIZE;
    return clubTeamRowsAll.slice(start, start + CLUBS_PAGE_SIZE);
  }, [clubTeamRowsAll, clubTeamPageSafe]);
  const matchTotal = useMemo(
    () => (payload?.matchesByStatus || []).reduce((s, r) => s + (Number(r.count) || 0), 0),
    [payload?.matchesByStatus]
  );

  const maxCanton = Math.max(1, ...(cantonRows.map((r) => r.registrations) || [1]));
  const maxSkill = Math.max(1, ...(skillRows.map((r) => r.teams) || [1]));
  const maxTrend = Math.max(1, ...(trendRows.map((r) => r.registrations) || [1]));

  const applyCanton = () => setCanton(cantonDraft.trim());

  const exportCurrent = () => {
    if (tab === 'canton') {
      const cantonCols = [
        { key: 'canton', label: 'Canton' },
        { key: 'registrations', label: 'Registrations' },
        { key: 'paid', label: 'Paid' },
        { key: 'active_teams', label: 'Active teams' },
      ];
      if (showFinancials) {
        cantonCols.push({ key: 'revenue_checkout_chf', label: 'Checkout CHF' });
      }
      downloadCsv('analytics-by-canton.csv', cantonRows, cantonCols);
    } else if (tab === 'skill') {
      downloadCsv('analytics-by-skill.csv', skillRows, [
        { key: 'band', label: 'Skill band' },
        { key: 'teams', label: 'Teams' },
      ]);
    } else if (tab === 'matches') {
      downloadCsv('analytics-matches.csv', matchRows, [
        { key: 'status', label: 'Status' },
        { key: 'count', label: 'Count' },
      ]);
    } else if (tab === 'groups') {
      downloadCsv('analytics-group-sizes.csv', groupRows, [
        { key: 'groupSize', label: 'Group size' },
        { key: 'divisions', label: 'Divisions' },
        { key: 'pct', label: 'Percent' },
      ]);
    } else if (tab === 'trends') {
      downloadCsv('analytics-trends.csv', trendRows, [
        { key: 'month', label: 'Month' },
        { key: 'registrations', label: 'Registrations' },
        { key: 'paid', label: 'Paid' },
      ]);
    } else if (tab === 'clubs') {
      downloadCsv('analytics-home-club.csv', clubNamedFiltered, [
        { key: 'club_label', label: 'Home club' },
        { key: 'teams', label: 'Teams' },
        { key: 'matches', label: 'Matches' },
        { key: 'completed_matches', label: 'Completed matches' },
        { key: 'avg_matches_per_team', label: 'Avg matches per team' },
      ]);
      window.setTimeout(() => {
        downloadCsv('analytics-home-club-teams.csv', payload?.homeClubTeams || [], [
          { key: 'team_name', label: 'Team' },
          { key: 'club_label', label: 'Home club' },
          { key: 'matches_played', label: 'Matches played' },
          { key: 'matches_completed', label: 'Matches completed' },
        ]);
      }, 250);
    } else {
      downloadCsv(
        'analytics-kpis.csv',
        [kpis],
        Object.keys(kpis).map((k) => ({ key: k, label: k }))
      );
    }
  };

  return (
    <Page>
      <Header>
        <TitleBlock>
          <h2>Analytics</h2>
          <p>
            League and operational insights from existing data. Club affiliation and forfeit counts are
            best-effort — see disclaimers on each section.
          </p>
        </TitleBlock>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Btn type="button" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Btn>
          <Btn type="button" $primary onClick={exportCurrent}>
            Export CSV
          </Btn>
        </div>
      </Header>

      <FilterBar>
        <AdminSeasonPhaseFilters
          campaignId={campaignId}
          seasonId={seasonId}
          seasonOptions={seasonOptions}
          phaseOptions={phaseOptions}
          onCampaignChange={onCampaignChange}
          onPhaseChange={onPhaseChange}
          allowAll={allowAll}
        />
        <div>
          <Label htmlFor="analytics-canton">Canton filter</Label>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <Input
              id="analytics-canton"
              placeholder="e.g. Geneva or GE"
              value={cantonDraft}
              onChange={(e) => setCantonDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyCanton()}
            />
            <Btn type="button" onClick={applyCanton}>
              Apply
            </Btn>
            {canton ? (
              <Btn
                type="button"
                onClick={() => {
                  setCanton('');
                  setCantonDraft('');
                }}
              >
                Clear
              </Btn>
            ) : null}
          </div>
        </div>
      </FilterBar>

      <Tabs>
        {TABS.map((t) => (
          <Tab key={t.id} type="button" $active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </Tab>
        ))}
      </Tabs>

      {isLoading && !payload ? <Empty>Loading analytics…</Empty> : null}
      {error ? (
        <Empty style={{ color: BRAND.red }}>
          {error?.response?.data?.message || error.message || 'Failed to load analytics'}
        </Empty>
      ) : null}

      {payload && tab === 'overview' ? (
        <>
          <Disclaimer>
            {seasonId
              ? 'Showing stats for the selected phase only. New teams = original signup in this phase. Carried = already seated in an earlier phase of the same season (first season phase = 0 carried).'
              : 'No phase selected — league-wide totals (all phases). Select a phase for new vs carried registration counts and phase-only matches.'}
          </Disclaimer>
          <KpiGrid>
            {seasonId ? (
              <>
                <Kpi>
                  <KpiLabel>New teams (this phase)</KpiLabel>
                  <KpiValue>{kpis.newTeamsInSeason ?? 0}</KpiValue>
                  <KpiHint>Original signup in this phase</KpiHint>
                </Kpi>
                <Kpi>
                  <KpiLabel>Carried teams (this phase)</KpiLabel>
                  <KpiValue>{kpis.carriedTeamsInSeason ?? 0}</KpiValue>
                  <KpiHint>On this phase roster, but original signup was another phase</KpiHint>
                </Kpi>
                <Kpi>
                  <KpiLabel>Active teams</KpiLabel>
                  <KpiValue>{kpis.activeTeams}</KpiValue>
                  <KpiHint>
                    Unique teams with an active seat
                    {kpis.withdrawnOrInactiveTeams
                      ? ` · ${kpis.withdrawnOrInactiveTeams} inactive`
                      : ''}
                  </KpiHint>
                </Kpi>
              </>
            ) : (
              <Kpi>
                <KpiLabel>Distinct active teams</KpiLabel>
                <KpiValue>{kpis.activeTeams}</KpiValue>
                <KpiHint>Across all phases — pick a phase for registration cohorts</KpiHint>
              </Kpi>
            )}
            <Kpi>
              <KpiLabel>Active roster players</KpiLabel>
              <KpiValue>{kpis.activePlayers}</KpiValue>
              <KpiHint>~2 per team when both members are active</KpiHint>
            </Kpi>
            <Kpi>
              <KpiLabel>Total matches</KpiLabel>
              <KpiValue>{kpis.totalMatches ?? '—'}</KpiValue>
              <KpiHint>{seasonId ? 'This phase only' : 'All phases'}</KpiHint>
            </Kpi>
            <Kpi>
              <KpiLabel>Completed matches</KpiLabel>
              <KpiValue>{kpis.completedMatches}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Remaining matches</KpiLabel>
              <KpiValue>{kpis.remainingMatches}</KpiValue>
            </Kpi>
            {showFinancials ? (
              <Kpi>
                <KpiLabel>Checkout revenue (CHF)</KpiLabel>
                <KpiValue>{Number(kpis.revenueTotalChf || 0).toFixed(2)}</KpiValue>
                <KpiHint>
                  {kpis.revenueCheckoutCount ?? 0} Stripe checkouts
                  {kpis.revenueCheckoutAvgFeeChf != null
                    ? ` · avg ${Number(kpis.revenueCheckoutAvgFeeChf).toFixed(0)}`
                    : ''}
                  {Number(kpis.revenueInvoicesChf || 0) > 0
                    ? ` · + invoices ${Number(kpis.revenueInvoicesChf).toFixed(0)}`
                    : ''}
                  {' · '}
                  {seasonId ? 'this phase only' : 'all phases'}
                </KpiHint>
              </Kpi>
            ) : null}
            <Kpi>
              <KpiLabel>Forfeit (heuristic)</KpiLabel>
              <KpiValue $alert={kpis.forfeitMatchesHeuristic > 0}>
                {kpis.forfeitMatchesHeuristic}
              </KpiValue>
            </Kpi>
            <Kpi
              as={kpis.openDisputes > 0 ? 'button' : 'div'}
              type={kpis.openDisputes > 0 ? 'button' : undefined}
              onClick={
                kpis.openDisputes > 0
                  ? () => {
                      setTab('matches');
                      setDrillStatus('disputed');
                    }
                  : undefined
              }
              style={
                kpis.openDisputes > 0
                  ? { cursor: 'pointer', textAlign: 'left', font: 'inherit', width: '100%' }
                  : undefined
              }
            >
              <KpiLabel>Open disputes</KpiLabel>
              <KpiValue $alert={kpis.openDisputes > 0}>{kpis.openDisputes}</KpiValue>
              {kpis.openDisputes > 0 ? (
                <KpiHint>Click to list &amp; fix scores</KpiHint>
              ) : null}
            </Kpi>
            <Kpi>
              <KpiLabel>Pending invites</KpiLabel>
              <KpiValue>{kpis.pendingInvitations}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Unassigned teams</KpiLabel>
              <KpiValue $alert={kpis.unassignedTeams > 0}>{kpis.unassignedTeams}</KpiValue>
            </Kpi>
          </KpiGrid>
          {showFinancials && disclaimers.revenue ? (
            <Disclaimer>{disclaimers.revenue}</Disclaimer>
          ) : null}
          <Disclaimer>{disclaimers.forfeitMatches}</Disclaimer>
        </>
      ) : null}

      {payload && tab === 'canton' ? (

        <Panel>
          <PanelTitle>Registrations by canton</PanelTitle>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
            Distinct teams (Original ⭐ when a phase is selected; unique teams for All phases). Not seats × phases.
          </p>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <SortableHead label="Canton" colKey="canton" sort={sort} onSort={onSort} />
                  <SortableHead label="Teams" colKey="registrations" sort={sort} onSort={onSort} />
                  <SortableHead label="Paid" colKey="paid" sort={sort} onSort={onSort} />
                  <SortableHead label="Active" colKey="active_teams" sort={sort} onSort={onSort} />
                  {showFinancials ? (
                    <SortableHead
                      label="Checkout CHF"
                      colKey="revenue_checkout_chf"
                      sort={sort}
                      onSort={onSort}
                    />
                  ) : null}
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {cantonRows.length === 0 ? (
                  <tr>
                    <td colSpan={showFinancials ? 6 : 5}>No data</td>
                  </tr>
                ) : (
                  cantonRows.map((r) => (
                    <tr key={r.canton}>
                      <td>{r.canton}</td>
                      <td>{r.registrations}</td>
                      <td>{r.paid}</td>
                      <td>{r.active_teams}</td>
                      {showFinancials ? (
                        <td>{Number(r.revenue_checkout_chf || r.revenue_tsr || 0).toFixed(2)}</td>
                      ) : null}
                      <td>
                        <BarTrack>
                          <BarFill $pct={(r.registrations / maxCanton) * 100} />
                        </BarTrack>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      ) : null}

      {payload && tab === 'skill' ? (
        <Panel>
          <PanelTitle>Teams by skill band</PanelTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <SortableHead label="Band" colKey="band" sort={sort} onSort={onSort} />
                  <SortableHead label="Teams" colKey="teams" sort={sort} onSort={onSort} />
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {skillRows.map((r) => (
                  <tr key={r.band}>
                    <td>{r.band}</td>
                    <td>{r.teams}</td>
                    <td>
                      <BarTrack>
                        <BarFill $pct={(r.teams / maxSkill) * 100} />
                      </BarTrack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      ) : null}

      {payload && tab === 'matches' ? (
        <Panel>
          <PanelTitle>Matches by status</PanelTitle>
          <Disclaimer>
            {disclaimers.forfeitMatches}
            {' '}
            Click <strong>score_submitted</strong> or <strong>disputed</strong> to list those matches and confirm / edit scores (same tools as Upcoming matches).
          </Disclaimer>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <SortableHead label="Status" colKey="status" sort={sort} onSort={onSort} />
                  <SortableHead label="Count" colKey="count" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {matchRows.map((r) => (
                  <tr key={r.status}>
                    <td>
                      {DRILLABLE_STATUSES.has(r.status) ? (
                        <ClickableStatus type="button" onClick={() => openDrill(r.status)}>
                          {r.status}
                          {drillStatus === r.status ? ' ▾' : ' →'}
                        </ClickableStatus>
                      ) : (
                        r.status
                      )}
                    </td>
                    <td>{r.count}</td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <strong>Total (all statuses)</strong>
                  </td>
                  <td>
                    <strong>{matchTotal}</strong>
                  </td>
                </tr>
              </tbody>
            </Table>
          </TableWrap>

          {drillStatus ? (
            <DrillPanel>
              <DrillHeader>
                <div>
                  <h4>
                    {drillStatus === 'score_submitted'
                      ? 'Score submitted — waiting for confirmation'
                      : 'Disputed matches'}
                  </h4>
                  <p>
                    {drillStatus === 'score_submitted'
                      ? 'One side submitted a score; opponent confirmation (or auto-confirm / admin confirm) is still pending.'
                      : 'Teams disagree on the score — open a match to edit or clear and resolve.'}
                  </p>
                </div>
                <Btn type="button" onClick={() => setDrillStatus(null)}>
                  Close list
                </Btn>
              </DrillHeader>
              {drillLoading ? (
                <Empty>Loading matches…</Empty>
              ) : drillMatches.length === 0 ? (
                <Empty>No matches in this status for the current filters.</Empty>
              ) : (
                <MatchList>
                  {drillMatches.map((m) => (
                    <MatchRow key={m.id}>
                      <MatchMeta>
                        <strong>
                          {m.team1_name || 'Team 1'} vs {m.team2_name || 'Team 2'}
                        </strong>
                        <span>
                          {[m.season_name, m.division_name || m.group_name, m.canton]
                            .filter(Boolean)
                            .join(' · ')}
                          {m.match_date ? ` · ${String(m.match_date).slice(0, 10)}` : ''}
                          {m.venue && m.venue !== 'TBD' ? ` · ${m.venue}` : ''}
                        </span>
                      </MatchMeta>
                      <Btn type="button" $primary onClick={() => setScoreModalMatch(m)}>
                        Open / fix
                      </Btn>
                    </MatchRow>
                  ))}
                </MatchList>
              )}
            </DrillPanel>
          ) : null}

          <AdminScoreModal
            isOpen={!!scoreModalMatch}
            match={scoreModalMatch}
            onClose={() => setScoreModalMatch(null)}
            onSuccess={onScoreModalSuccess}
          />
        </Panel>
      ) : null}

      {payload && tab === 'groups' ? (
        <Panel>
          <PanelTitle>Group size distribution</PanelTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <SortableHead label="Size" colKey="groupSize" sort={sort} onSort={onSort} />
                  <SortableHead label="Divisions" colKey="divisions" sort={sort} onSort={onSort} />
                  <SortableHead label="%" colKey="pct" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {groupRows.map((r) => (
                  <tr key={r.groupSize}>
                    <td>{r.groupSize} teams</td>
                    <td>{r.divisions}</td>
                    <td>{r.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      ) : null}

      {payload && tab === 'trends' ? (
        <Panel>
          <PanelTitle>Registration trend (12 months)</PanelTitle>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#64748b' }}>
            Each team once — month of their first real signup (excludes phase carry). Sum of months ≈ unique teams.
          </p>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <SortableHead label="Month" colKey="month" sort={sort} onSort={onSort} />
                  <SortableHead label="Registrations" colKey="registrations" sort={sort} onSort={onSort} />
                  <SortableHead label="Paid" colKey="paid" sort={sort} onSort={onSort} />
                  <th>Volume</th>
                </tr>
              </thead>
              <tbody>
                {trendRows.map((r) => (
                  <tr key={r.month}>
                    <td>{r.month}</td>
                    <td>{r.registrations}</td>
                    <td>{r.paid}</td>
                    <td>
                      <BarTrack>
                        <BarFill $pct={(r.registrations / maxTrend) * 100} />
                      </BarTrack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      ) : null}

      {payload && tab === 'ops' ? (
        <>
          <Disclaimer>{disclaimers.withdrawals}</Disclaimer>
          <KpiGrid>
            <Kpi>
              <KpiLabel>Withdrawn / inactive</KpiLabel>
              <KpiValue $alert>{kpis.withdrawnOrInactiveTeams}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Pending registrations</KpiLabel>
              <KpiValue>{kpis.registrationsPending}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Open disputes</KpiLabel>
              <KpiValue $alert={kpis.openDisputes > 0}>{kpis.openDisputes}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Pending invites</KpiLabel>
              <KpiValue>{kpis.pendingInvitations}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Unassigned teams</KpiLabel>
              <KpiValue $alert={kpis.unassignedTeams > 0}>{kpis.unassignedTeams}</KpiValue>
            </Kpi>
            <Kpi>
              <KpiLabel>Remaining matches</KpiLabel>
              <KpiValue>{kpis.remainingMatches}</KpiValue>
            </Kpi>
          </KpiGrid>
        </>
      ) : null}

      {payload && tab === 'clubs' ? (
        <Panel>
          <PanelTitle>Home club (by team → matches)</PanelTitle>
          <Disclaimer>{disclaimers.homeClub}</Disclaimer>

          <NotSetCard>
            <NotSetStat>
              Teams that played
              <strong>{clubMeta.teamsWithMatches || 0}</strong>
            </NotSetStat>
            <NotSetStat>
              League matches
              <strong>{clubMeta.leagueMatches || 0}</strong>
            </NotSetStat>
            {clubNotSet ? (
              <>
                <NotSetStat>
                  No club set — teams
                  <strong>
                    {clubNotSet.teams}
                    {clubMeta.teamsWithMatches
                      ? ` / ${clubMeta.teamsWithMatches}`
                      : ''}
                  </strong>
                </NotSetStat>
                <NotSetStat>
                  No club — matches
                  <strong>{clubNotSet.matches ?? clubNotSet.team_matches}</strong>
                </NotSetStat>
                <NotSetStat>
                  Avg matches / no-club team
                  <strong>{clubNotSet.avg_matches_per_team ?? '—'}</strong>
                </NotSetStat>
              </>
            ) : null}
          </NotSetCard>

          <FilterBar style={{ marginBottom: '0.85rem', boxShadow: 'none' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <Label htmlFor="club-search">Search club</Label>
              <Input
                id="club-search"
                placeholder="e.g. Chambésy, Fribourg, Urban…"
                value={clubSearch}
                onChange={(e) => {
                  setClubSearch(e.target.value);
                  setClubPage(1);
                }}
              />
            </div>
            <div style={{ alignSelf: 'flex-end', fontSize: '0.8rem', color: BRAND.muted }}>
              {clubNamedFiltered.length} club{clubNamedFiltered.length === 1 ? '' : 's'}
            </div>
          </FilterBar>

          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th style={{ cursor: 'default' }}>#</th>
                  <SortableHead label="Club text" colKey="club_label" sort={sort} onSort={onSort} />
                  <SortableHead label="Teams" colKey="teams" sort={sort} onSort={onSort} />
                  <SortableHead label="Matches" colKey="matches" sort={sort} onSort={onSort} />
                  <SortableHead
                    label="Completed"
                    colKey="completed_matches"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHead
                    label="Avg / team"
                    colKey="avg_matches_per_team"
                    sort={sort}
                    onSort={onSort}
                  />
                  <th style={{ cursor: 'default' }}>Teams list</th>
                </tr>
              </thead>
              <tbody>
                {clubRowsPage.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ color: BRAND.muted }}>
                      No clubs match this search.
                    </td>
                  </tr>
                ) : (
                  clubRowsPage.map((r, idx) => (
                    <tr key={r.club_key}>
                      <td>{(clubPageSafe - 1) * CLUBS_PAGE_SIZE + idx + 1}</td>
                      <td>{r.club_label}</td>
                      <td>{r.teams}</td>
                      <td>{r.matches ?? r.team_matches}</td>
                      <td>{r.completed_matches}</td>
                      <td>{r.avg_matches_per_team}</td>
                      <td>
                        <Btn
                          type="button"
                          $primary={clubDetailKey === r.club_key}
                          onClick={() => {
                            setClubDetailKey(r.club_key);
                            setClubTeamPage(1);
                            setClubTeamSearch('');
                            // Defer scroll until filtered section re-renders
                            window.setTimeout(() => {
                              clubTeamsSectionRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              });
                            }, 50);
                          }}
                        >
                          {clubDetailKey === r.club_key ? 'Showing' : 'View'} {r.teams}
                        </Btn>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>

          <Pager>
            <span>
              Page {clubPageSafe} of {clubPageCount}
              {clubNamedFiltered.length
                ? ` · showing ${(clubPageSafe - 1) * CLUBS_PAGE_SIZE + 1}–${Math.min(
                    clubPageSafe * CLUBS_PAGE_SIZE,
                    clubNamedFiltered.length
                  )} of ${clubNamedFiltered.length}`
                : ''}
            </span>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <Btn
                type="button"
                disabled={clubPageSafe <= 1}
                onClick={() => setClubPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Btn>
              <Btn
                type="button"
                disabled={clubPageSafe >= clubPageCount}
                onClick={() => setClubPage((p) => Math.min(clubPageCount, p + 1))}
              >
                Next
              </Btn>
            </div>
          </Pager>

          {clubDetailKey ? (
            <NotSetCard style={{ marginTop: '1rem', background: '#eff6ff', borderColor: '#bfdbfe' }}>
              <NotSetStat style={{ color: '#1e40af' }}>
                Showing teams for
                <strong style={{ color: '#043663' }}>
                  {clubRowsAll.find((c) => c.club_key === clubDetailKey)?.club_label || clubDetailKey}
                </strong>
              </NotSetStat>
              <NotSetStat style={{ color: '#1e40af' }}>
                Teams found
                <strong style={{ color: '#043663' }}>{clubTeamRowsAll.length}</strong>
              </NotSetStat>
              <div style={{ alignSelf: 'center' }}>
                <Btn
                  type="button"
                  onClick={() => {
                    setClubDetailKey(null);
                    setClubTeamPage(1);
                  }}
                >
                  Clear filter
                </Btn>
              </div>
            </NotSetCard>
          ) : null}

          <div ref={clubTeamsSectionRef}>
          <PanelTitle style={{ marginTop: '1.5rem' }}>
            Per team
            {clubDetailKey
              ? ` · filtered: ${
                  clubRowsAll.find((c) => c.club_key === clubDetailKey)?.club_label || clubDetailKey
                }`
              : ''}
          </PanelTitle>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: BRAND.muted }}>
            Each row is one team that played in this filter. “Matches played” is how many of their
            phase matches carry this club label (club is from the roster profile, so usually all of
            their matches, e.g. 6/6).
          </p>

          <FilterBar style={{ marginBottom: '0.85rem', boxShadow: 'none' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <Label htmlFor="club-team-search">Search team or club</Label>
              <Input
                id="club-team-search"
                placeholder="Team name or club…"
                value={clubTeamSearch}
                onChange={(e) => {
                  setClubTeamSearch(e.target.value);
                  setClubTeamPage(1);
                }}
              />
            </div>
            {clubDetailKey ? (
              <div style={{ alignSelf: 'flex-end' }}>
                <Btn
                  type="button"
                  onClick={() => {
                    setClubDetailKey(null);
                    setClubTeamPage(1);
                  }}
                >
                  Clear club filter
                </Btn>
              </div>
            ) : null}
            <div style={{ alignSelf: 'flex-end', fontSize: '0.8rem', color: BRAND.muted }}>
              {clubTeamRowsAll.length} team{clubTeamRowsAll.length === 1 ? '' : 's'}
            </div>
          </FilterBar>

          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th style={{ cursor: 'default' }}>#</th>
                  <SortableHead label="Team" colKey="team_name" sort={sort} onSort={onSort} />
                  <SortableHead label="Home club" colKey="club_label" sort={sort} onSort={onSort} />
                  <SortableHead
                    label="Matches played"
                    colKey="matches_played"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHead
                    label="Completed"
                    colKey="matches_completed"
                    sort={sort}
                    onSort={onSort}
                  />
                  <th style={{ cursor: 'default' }}>Club coverage</th>
                </tr>
              </thead>
              <tbody>
                {clubTeamRowsPage.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: BRAND.muted }}>
                      No teams match this search.
                    </td>
                  </tr>
                ) : (
                  clubTeamRowsPage.map((r, idx) => (
                    <tr key={r.team_id}>
                      <td>{(clubTeamPageSafe - 1) * CLUBS_PAGE_SIZE + idx + 1}</td>
                      <td>{r.team_name}</td>
                      <td>{r.club_label}</td>
                      <td>{r.matches_played}</td>
                      <td>{r.matches_completed}</td>
                      <td>
                        {r.matches_played > 0
                          ? `${r.matches_played}/${r.matches_played} matches with this club`
                          : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>

          <Pager>
            <span>
              Page {clubTeamPageSafe} of {clubTeamPageCount}
              {clubTeamRowsAll.length
                ? ` · showing ${(clubTeamPageSafe - 1) * CLUBS_PAGE_SIZE + 1}–${Math.min(
                    clubTeamPageSafe * CLUBS_PAGE_SIZE,
                    clubTeamRowsAll.length
                  )} of ${clubTeamRowsAll.length}`
                : ''}
            </span>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <Btn
                type="button"
                disabled={clubTeamPageSafe <= 1}
                onClick={() => setClubTeamPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Btn>
              <Btn
                type="button"
                disabled={clubTeamPageSafe >= clubTeamPageCount}
                onClick={() => setClubTeamPage((p) => Math.min(clubTeamPageCount, p + 1))}
              >
                Next
              </Btn>
            </div>
          </Pager>
          </div>
        </Panel>
      ) : null}
    </Page>
  );
}
